import React, { useEffect, useState, useCallback, cloneElement } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { Manager, Reference, Popper } from 'react-popper';
import useStyle from './TooltipStyles';
import usePlacement from './usePlacement';

/**
 * Tooltip element for use in Cvent-branded interfaces. Requires a trigger prop
 * to open and close the tooltip. The contents of the tooltip are supplied to
 * the text prop.
 */

export const Tooltip = ({
  accessibilityLabel,
  defaultOpen = false,
  isRtl = false,
  onClose = () => {},
  onOpen = () => {},
  placement = 'bottom',
  portal,
  preventOverflow = true,
  testID,
  text,
  trigger,
  isFixed = false
}) => {
  const position = usePlacement(placement, isRtl);
  const [isOpen, setIsOpen] = useState(defaultOpen);

  useEffect(() => {
    const handleEsc = event => {
      if (event.keyCode === 27 && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen]);

  const {
    caret,
    caretPosition,
    triggerWrapper,
    tooltipWrapper,
    tooltipPosition
  } = useStyle(isRtl);

  const handleOpen = useCallback(() => {
    onOpen();
    setIsOpen(true);
  }, [onOpen]);

  const handleClose = useCallback(() => {
    onClose();
    setIsOpen(false);
  }, [onClose]);

  const renderPopper = () => {
    const portalRef = portal;
    const popperAndContent = (
      <Popper
        placement={position}
        positionFixed={isFixed}
        modifiers={{
          preventOverflow: {
            enabled: preventOverflow
          },
          hide: {
            enabled: preventOverflow
          }
        }}
      >
        {({ ref, style, placement: arrowPlacement, arrowProps }) => (
          <div
            ref={ref}
            data-placement={position}
            css={{
              ...tooltipWrapper,
              ...tooltipPosition[
                arrowPlacement && arrowPlacement.split('-')[0]
              ],
              ...style
            }}
            {...injectTestId(testID)}
          >
            <div
              ref={arrowProps.ref}
              css={{
                ...arrowProps.style,
                ...caret,
                ...caretPosition[arrowPlacement && arrowPlacement.split('-')[0]]
              }}
              aria-label={accessibilityLabel}
              role="tooltip"
            />
            {text}
          </div>
        )}
      </Popper>
    );
    if (portalRef) {
      const portalTarget =
        portalRef === true
          ? typeof window !== 'undefined' && window.document.body
          : portalRef;
      // portal is wrapped in div to suppress prop-type warning from Manager
      return ReactDOM.createPortal(popperAndContent, portalTarget);
    }
    return popperAndContent;
  };

  return (
    <Manager>
      <Reference>
        {({ ref }) => (
          <div ref={ref} css={triggerWrapper}>
            {typeof trigger === 'function'
              ? trigger(handleOpen, handleClose)
              : cloneElement(trigger, {
                  tabIndex: 0,
                  onMouseOver: handleOpen,
                  onMouseLeave: handleClose,
                  onFocus: handleOpen,
                  onBlur: handleClose
                })}
          </div>
        )}
      </Reference>
      {isOpen && renderPopper()}
    </Manager>
  );
};

Tooltip.propTypes = {
  /** Text content displayed within the tooltip */
  text: PropTypes.string.isRequired,
  /** Content that triggers the tooltip
   * For basic use-cases, you can pass in your html element or component directly
   * If you need more control over open/close events, use a function with render props
   * Example:
   * const [disabled, setDisabled] = useState(false);
   *
   * <Tooltip
   *  trigger={(handleOpen, handleClose) => (
   *    <button
   *      onMouseOver={!disabled && handleOpen}
   *      onMouseLeave={!disabled && handleClose}
   *    >
   *     Open
   *    </button>
   *  )}
   * />
   */
  trigger: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.func
  ]).isRequired,
  /** Screen reader label text (e.g. aria-label) */
  accessibilityLabel: PropTypes.string,
  /** defaultOpen will set the popover to open upon component mount */
  defaultOpen: PropTypes.bool,
  /** isFixed will switch popover positioning from absolute to fixed.
   *  If you''re seeing the popover affect surrounding containers or cause overflow problems,
   *  use this setting to possibly resolve.
   */
  isFixed: PropTypes.bool,
  /** Text direction */
  isRtl: PropTypes.bool,
  /** Call a function upon the Popover closing. No return value. */
  onClose: PropTypes.func,
  /** Call a function upon the Popover opening. No return value. */
  onOpen: PropTypes.func,
  /** Initial placement for the tooltip. Default is bottom. */
  placement: PropTypes.oneOf([
    'auto',
    'top',
    'top-start',
    'top-end',
    'end',
    'end-top',
    'end-bottom',
    'bottom',
    'bottom-start',
    'bottom-end',
    'start',
    'start-top',
    'start-bottom'
  ]),
  /** DOM node reference to render menu inside. If boolean 'true' is provided, Menu will be appended to the 'body' element. */
  portal: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  /** Set preventOverflow to true if you wish the Popover to flow outside the container */
  preventOverflow: PropTypes.bool,
  /** For e2e tests (maps to `data-cvent-id`) */
  testID: PropTypes.string
};
